import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Fade, FormControlLabel, Radio, RadioGroup,
} from '@material-ui/core';
import { Grid, Label } from '../../common/components';
import messages from '../messages';
import ReservationPicker from './ReservationPicker';

const S = {
  RadioGroup: styled(RadioGroup)`
    flex-direction: row;
  `,
  Radio: styled(Radio)`
    color: ${({ theme }) => theme.palette.grey[600]};
    &.Mui-checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  FormControlLabel: styled(FormControlLabel)`
    :not(:last-child) {
      margin-right: 40px;
    }
  `,
  Label: styled(Label)`
    min-height: 42px;
  `,
  Date: styled.div`
    ${({ visible }) => !visible && css`
      position: absolute;
    `}
  `,
  HelperText: styled.p`
    margin-top: 8px;
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
};

const ReservationField = ({
  className, field: { onChange, ...field }, form, intl,
}) => {
  const [value, setValue] = useState('now');

  const error = useMemo(() => !!(
    form.errors[field.name] && form.touched[field.name]
  ), [field.name, form.errors, form.touched]);

  const handleChange = useCallback(event => setValue(event.target.value), []);

  return (
    <Grid
      container
      alignItems="flex-start"
      spacing={2}
      className={className}
    >
      <Grid item xs={1}>
        <S.Label
          htmlFor={field.name}
          error={error}
        >
          <FormattedMessage {...messages.reservationFieldLabel} />
        </S.Label>
      </Grid>
      <Grid item xs={6}>
        <S.RadioGroup
          aria-label={intl.formatMessage(messages.reservationFieldRadioGroupAriaLabel)}
          name="date"
          value={value}
          onChange={handleChange}
        >
          <S.FormControlLabel
            value="now"
            control={<S.Radio color="primary" />}
            label={intl.formatMessage(messages.reservationFieldRadioNow)}
          />
          <S.FormControlLabel
            value="reservation"
            control={<S.Radio color="primary" />}
            label={intl.formatMessage(messages.reservationFieldRadioReservation)}
          />
        </S.RadioGroup>
        <Fade in={value === 'reservation'}>
          <S.Date visible={value === 'reservation'}>
            <S.HelperText>
              <FormattedMessage {...messages.reservationFieldHelperText} />
            </S.HelperText>
            <ReservationPicker
              field={field}
              form={form}
            />
          </S.Date>
        </Fade>
      </Grid>
    </Grid>
  );
};

ReservationField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.shape({}),
    ]).isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({
      title: PropTypes.string,
    }).isRequired,
    touched: PropTypes.shape({
      title: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

ReservationField.defaultProps = {
  className: undefined,
};

export default injectIntl(ReservationField);
